import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeInSection } from '../components/FadeInSection';

interface FaqItem {
  question: string;
  answer: string;
}

const FAQS_GENERAL: FaqItem[] = [
  {
    question: 'What should I bring to my first appointment?',
    answer:
      'Please bring your medical aid card, a valid form of ID, any previous scan or lab results, a list of current medications, and your referral letter if applicable. If you are pregnant, bring your Road to Health booklet if you have one.',
  },
  {
    question: 'Do I need a referral to see Dr Khan?',
    answer:
      'A referral is not required for a private consultation, but some medical aids may require one for reimbursement. Check with your medical aid before your appointment.',
  },
  {
    question: 'How do I get my results?',
    answer:
      "Results are discussed at a follow-up consultation or communicated via phone for routine results. Dr Khan's practice will contact you when results are available.",
  },
  {
    question: 'What medical aids does Dr Khan accept?',
    answer:
      "Dr Khan is registered with most major medical aids including Discovery, Bonitas, Medihelp, Momentum, and Fedhealth. Please confirm your specific plan's cover with your medical aid before your appointment.",
  },
  {
    question: 'Is there parking available?',
    answer: 'Yes, free parking is available on-site at both Melomed Gatesville and Melomed Mitchell\'s Plain.',
  },
];

const FAQS_OBSTETRICS: FaqItem[] = [
  {
    question: 'When should I book my first antenatal appointment?',
    answer:
      'As soon as you have a positive pregnancy test — ideally before 10 weeks. Early booking allows for first trimester screening, dating scans, and early identification of any risk factors.',
  },
  {
    question: 'How many antenatal visits will I need?',
    answer:
      'For a low-risk pregnancy, you can expect approximately 8–10 visits throughout your pregnancy. High-risk pregnancies may require more frequent monitoring. A typical schedule is: booking visit (before 10 weeks), 12 weeks (NT scan), 16 weeks, 20 weeks (anomaly scan), 24 weeks, 28 weeks, 32 weeks, 36 weeks, 38 weeks, 40 weeks.',
  },
  {
    question: 'What is the difference between a normal and a high-risk pregnancy?',
    answer:
      'A high-risk pregnancy is one where the mother or baby has an increased chance of complications. Risk factors include age over 35, twins or multiples, pre-existing conditions (diabetes, hypertension, thyroid disease), previous pregnancy complications, or problems identified on scan. Dr Khan specialises in high-risk obstetrics and will monitor you more closely if needed.',
  },
  {
    question: 'What supplements should I take during pregnancy?',
    answer:
      'Folic acid supplementation is recommended before conception and during early pregnancy. The appropriate dose depends on your individual risk factors — Dr Khan will advise you. Do not take any supplements or medications without discussing with your doctor first.',
  },
  {
    question: 'What symptoms should I be concerned about during pregnancy?',
    answer:
      'Go to your nearest emergency room immediately if you experience: heavy vaginal bleeding, severe abdominal pain, severe headache with visual disturbance, sudden swelling of the face or hands, reduced or absent foetal movements after 28 weeks, leaking fluid, or fever above 38°C.',
  },
  {
    question: 'Can I exercise during pregnancy?',
    answer:
      'Yes — regular moderate exercise is encouraged for most pregnant women. Walking, swimming, and prenatal yoga are ideal. Avoid contact sports, high-impact activities, and exercises that involve lying flat on your back after 16 weeks. Always discuss your exercise plan with Dr Khan first.',
  },
  {
    question: 'What is a glucose tolerance test (GTT)?',
    answer:
      'The GTT screens for gestational diabetes. It involves a fasting blood test, drinking a glucose solution, and blood tests at 1 and 2 hours. It is routinely done between 24–28 weeks. Women with risk factors may be tested earlier.',
  },
  {
    question: 'What is pre-eclampsia?',
    answer:
      'Pre-eclampsia is a pregnancy complication characterised by high blood pressure and signs of damage to other organ systems, most often the kidneys. Symptoms include persistent headache, visual disturbance, upper abdominal pain, and sudden swelling. It typically occurs after 20 weeks and requires close monitoring. In severe cases, early delivery may be necessary.',
  },
  {
    question: 'When will I find out the sex of my baby?',
    answer:
      'The sex can usually be determined at the 20-week anomaly scan, provided the baby is in a cooperative position. Some parents prefer not to know — just let Dr Khan know at your scan.',
  },
  {
    question: 'What happens if I go past my due date?',
    answer:
      'Most babies arrive within 2 weeks either side of the due date. If you reach 41 weeks, induction of labour is usually recommended to reduce the risk of stillbirth and other complications. Dr Khan will discuss your options with you.',
  },
];

const FAQS_GYNAE: FaqItem[] = [
  {
    question: 'How often should I have a Pap smear?',
    answer:
      'Current South African guidelines recommend a Pap smear every 3 years for women aged 30–65 who have had normal results. Women who are HIV-positive or immunocompromised should be screened more frequently. Speak to Dr Khan about the right schedule for you.',
  },
  {
    question: 'What is an abnormal Pap smear result?',
    answer:
      'An abnormal result does not mean you have cancer. It means there are cell changes on the cervix that need further investigation. Dr Khan may recommend a colposcopy — a closer examination of the cervix — to determine the degree of change and whether treatment is needed.',
  },
  {
    question: 'What is endometriosis?',
    answer:
      'Endometriosis is a condition where tissue similar to the lining of the uterus grows outside the uterus — commonly on the ovaries, fallopian tubes, and pelvic lining. It can cause severe period pain, pelvic pain, painful intercourse, and fertility problems. It is diagnosed by laparoscopy and managed with medication or surgery depending on severity.',
  },
  {
    question: 'What causes irregular periods?',
    answer:
      'Irregular periods can be caused by many things including stress, significant weight changes, thyroid problems, PCOS, perimenopause, or structural issues in the uterus such as fibroids or polyps. A consultation with Dr Khan including blood tests and an ultrasound can usually identify the cause.',
  },
  {
    question: 'What is PCOS?',
    answer:
      'Polycystic Ovary Syndrome (PCOS) is a hormonal condition affecting women of reproductive age. Common features include irregular periods, excess androgen (causing acne, excess hair growth), and polycystic ovaries on ultrasound. It is also associated with insulin resistance. PCOS is managed with lifestyle changes, medication, and close monitoring — especially if fertility is desired.',
  },
  {
    question: 'What are fibroids?',
    answer:
      'Uterine fibroids are non-cancerous growths of muscle tissue in or around the uterus. They are very common and many women have no symptoms. When symptomatic, they can cause heavy periods, pelvic pressure, frequent urination, or fertility problems. Treatment options range from medication to minimally invasive surgery depending on size, location, and symptoms.',
  },
  {
    question: 'What is menopause and when does it happen?',
    answer:
      'Menopause is defined as 12 consecutive months without a period and typically occurs between ages 45–55. Perimenopause (the transition phase) can begin several years earlier. Common symptoms include hot flushes, night sweats, mood changes, vaginal dryness, and sleep disturbance. Hormone Replacement Therapy (HRT) and non-hormonal options are available — Dr Khan will help you find the right approach.',
  },
  {
    question: 'What contraceptive options are available?',
    answer:
      "Dr Khan can advise on and fit a range of contraceptive options including: the oral contraceptive pill, the hormonal IUD (Mirena), the copper IUD, the contraceptive implant (Implanon), the injectable (Depo-Provera), and barrier methods. The best option depends on your health history, lifestyle, and future pregnancy plans.",
  },
];

function FaqAccordion({ title, items }: { title: string; items: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="mb-10">
      <h3
        className="font-heading text-2xl mb-4"
        style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 500 }}
      >
        {title}
      </h3>
      <div className="space-y-2">
        {items.map((faq, idx) => (
          <div
            key={idx}
            className="rounded-lg overflow-hidden border"
            style={{ borderColor: openIndex === idx ? 'var(--color-gold)' : '#e5e7eb' }}
          >
            <button
              className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors"
              style={{
                backgroundColor: openIndex === idx ? 'var(--color-gold-light)' : '#fff',
                fontFamily: 'var(--font-body)',
              }}
              onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
              aria-expanded={openIndex === idx}
            >
              <span
                className="font-body font-medium text-sm"
                style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}
              >
                {faq.question}
              </span>
              {/* Gold chevron rotates on open */}
              <motion.svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="flex-shrink-0"
                style={{ color: 'var(--color-gold)' }}
                animate={{ rotate: openIndex === idx ? 180 : 0 }}
                transition={{ duration: 0.25 }}
              >
                <polyline points="6 9 12 15 18 9" />
              </motion.svg>
            </button>

            <AnimatePresence initial={false}>
              {openIndex === idx && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  style={{ overflow: 'hidden' }}
                >
                  <div
                    className="px-5 pb-4 pt-2 text-sm font-body leading-relaxed border-l-4"
                    style={{
                      color: 'var(--color-navy)',
                      fontFamily: 'var(--font-body)',
                      opacity: 0.8,
                      borderLeftColor: 'var(--color-gold)',
                    }}
                  >
                    {faq.answer}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ServicesPage() {
  useEffect(() => {
    document.title = 'Services | IKhanGynae';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Obstetrics, gynaecology, and laparoscopic surgery services offered by Dr Imraan Khan in Cape Town Southern Suburbs.'
      );
    }
  }, []);

  const obstetrics = [
    'Antenatal care (routine and high-risk)',
    'Twin and multiple pregnancies',
    'Gestational diabetes management',
    'Pre-eclampsia monitoring and management',
    'Foetal growth restriction',
    'Vaginal birth and elective caesarean section',
    'Postpartum care',
  ];

  const gynaecology = [
    'General gynaecological consultations',
    'Abnormal uterine bleeding',
    'Polycystic ovary syndrome (PCOS)',
    'Endometriosis diagnosis and management',
    'Uterine fibroids',
    'Ovarian cysts',
    'Cervical screening (Pap smears) and colposcopy',
    'Sexually transmitted infection screening',
    'Contraception counselling and fitting (IUD, implant)',
    'Menopause management and HRT',
    'Pelvic floor disorders',
  ];

  const procedures = [
    'Diagnostic and operative laparoscopy',
    'Hysteroscopy',
    'Colposcopy and cervical biopsy',
    'IUD insertion and removal',
    'Endometrial biopsy',
    'Vulval biopsy',
  ];

  return (
    <div>
      {/* ── Hero Banner ── */}
      <section
        className="py-20 px-4 text-white text-center relative overflow-hidden"
        style={{ backgroundColor: 'var(--color-navy)' }}
      >
        <img
          src="/images/logo-symbol-only.png"
          alt=""
          aria-hidden="true"
          className="absolute pointer-events-none select-none"
          style={{ width: 300, right: 16, top: '50%', transform: 'translateY(-50%)', opacity: 0.07 }}
        />
        <div className="relative z-10">
          <p
            className="font-heading italic mb-2"
            style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontSize: '1rem' }}
          >
            Comprehensive Women's Health Care
          </p>
          <h1
            className="font-heading"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 300,
              fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
              color: '#fff',
            }}
          >
            Services
          </h1>
          <div
            className="mx-auto mt-4"
            style={{ width: 60, height: 2, backgroundColor: 'var(--color-gold)', opacity: 0.7 }}
          />
        </div>
      </section>

      {/* ── Obstetrics ── */}
      <section className="py-16 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            <div className="flex-1">
              <FadeInSection>
                <h2
                  className="font-heading mb-6"
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 400,
                    fontSize: 'clamp(2rem, 3vw, 2.5rem)',
                    color: 'var(--color-navy)',
                  }}
                >
                  Obstetrics
                </h2>
                <div className="flex flex-wrap gap-2">
                  {obstetrics.map((item) => (
                    <span
                      key={item}
                      className="px-3 py-1.5 rounded-full text-xs font-body font-medium"
                      style={{
                        backgroundColor: 'var(--color-primary-light)',
                        color: 'var(--color-primary)',
                        fontFamily: 'var(--font-body)',
                        border: '1px solid var(--color-primary)',
                      }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </FadeInSection>
            </div>
            <FadeInSection delay={0.1} className="flex-shrink-0 w-full md:w-80">
              <img
                src="/images/surgical-portrait.jpg?v=2"
                alt="Dr Khan in theatre"
                className="w-full rounded-2xl shadow-md object-cover"
                style={{ maxHeight: 280 }}
              />
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Image divider — surgical team with teal overlay */}
      <div className="w-full overflow-hidden relative" style={{ maxHeight: 260 }}>
        <img
          src="/images/surgical-team.jpg?v=2"
          alt="Surgical team in theatre"
          className="w-full object-cover"
          style={{ maxHeight: 260, objectPosition: 'center 30%' }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: 'var(--color-primary)', opacity: 0.5 }} />
      </div>

      {/* ── Gynaecology ── */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading mb-6"
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 400,
                fontSize: 'clamp(2rem, 3vw, 2.5rem)',
                color: 'var(--color-navy)',
              }}
            >
              Gynaecology
            </h2>
            <div className="flex flex-wrap gap-2">
              {gynaecology.map((item) => (
                <span
                  key={item}
                  className="px-3 py-1.5 rounded-full text-xs font-body font-medium"
                  style={{
                    backgroundColor: 'var(--color-gold-light)',
                    color: 'var(--color-navy)',
                    fontFamily: 'var(--font-body)',
                    border: '1px solid var(--color-gold)',
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Image divider */}
      <div className="w-full overflow-hidden" style={{ maxHeight: 260 }}>
        <img
          src="/images/clinic_interior3.png"
          alt="IKhanGynae clinic interior"
          className="w-full object-cover"
          style={{ maxHeight: 260, objectPosition: 'center 40%' }}
        />
      </div>

      {/* ── Procedures ── */}
      <section className="py-16 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-5xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading mb-6"
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 400,
                fontSize: 'clamp(2rem, 3vw, 2.5rem)',
                color: 'var(--color-navy)',
              }}
            >
              Procedures
            </h2>
            <div className="flex flex-wrap gap-2">
              {procedures.map((item) => (
                <span
                  key={item}
                  className="px-3 py-1.5 rounded-full text-xs font-body font-medium"
                  style={{
                    backgroundColor: 'var(--color-primary-light)',
                    color: 'var(--color-primary)',
                    fontFamily: 'var(--font-body)',
                    border: '1px solid var(--color-primary)',
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ── What to Expect ── */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            <FadeInSection delay={0.1} className="flex-shrink-0 w-full md:w-72">
              <img
                src="/images/clinic_interior3.png"
                alt="Consultation room"
                className="w-full rounded-2xl shadow-md object-cover"
                style={{ maxHeight: 280 }}
              />
            </FadeInSection>
            <FadeInSection className="flex-1">
              <h2
                className="font-heading mb-4"
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 400,
                  fontSize: 'clamp(1.8rem, 3vw, 2.2rem)',
                  color: 'var(--color-navy)',
                }}
              >
                What to Expect at Your First Visit
              </h2>
              <p className="text-sm leading-relaxed mb-3 font-body" style={{ color: 'var(--color-navy)', opacity: 0.75, fontFamily: 'var(--font-body)' }}>
                Your first consultation with Dr Khan will cover a full medical and gynaecological history, your concerns, and any relevant examinations or investigations.
              </p>
              <p className="text-sm leading-relaxed mb-3 font-body" style={{ color: 'var(--color-navy)', opacity: 0.75, fontFamily: 'var(--font-body)' }}>
                For obstetric bookings, a dating scan and first trimester blood panel are usually arranged at or shortly after the first visit.
              </p>
              <p className="text-sm leading-relaxed font-body" style={{ color: 'var(--color-navy)', opacity: 0.75, fontFamily: 'var(--font-body)' }}>
                Please arrive 10 minutes early to complete registration paperwork. New patients can also{' '}
                <Link to="/register" className="underline font-medium" style={{ color: 'var(--color-primary)' }}>
                  pre-register online
                </Link>.
              </p>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ── FAQ Accordion ── */}
      <section className="py-16 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-4xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading text-center mb-10"
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 400,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                color: 'var(--color-navy)',
              }}
            >
              Frequently Asked Questions
            </h2>
          </FadeInSection>
          <FaqAccordion title="General" items={FAQS_GENERAL} />
          <FaqAccordion title="Obstetrics &amp; Pregnancy" items={FAQS_OBSTETRICS} />
          <FaqAccordion title="Gynaecology" items={FAQS_GYNAE} />
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        className="py-16 px-4 text-center"
        style={{ backgroundColor: 'var(--color-navy)' }}
      >
        <FadeInSection>
          <h2
            className="font-heading text-white mb-3"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 300,
              fontSize: 'clamp(2rem, 4vw, 3rem)',
            }}
          >
            Book your consultation today
          </h2>
          <p className="text-white/70 mb-6 font-body" style={{ fontFamily: 'var(--font-body)' }}>
            Contact Dr Khan's practice to schedule your appointment.
          </p>
          <Link
            to="/book"
            className="inline-block px-8 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity font-body"
            style={{
              backgroundColor: 'var(--color-gold)',
              color: 'var(--color-navy)',
              fontFamily: 'var(--font-body)',
            }}
          >
            Book your consultation →
          </Link>
        </FadeInSection>
      </section>
    </div>
  );
}
